import React, { useMemo, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { router } from 'expo-router';
import { useAuthStore, useChatStore } from '@/store';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { recentDiscoveryIdeas } from '@/lib/discoveryExperience';
import useDiscoveryContinuations from '@/hooks/useDiscoveryContinuations';
import { queueDiscoveryContinuation } from '@/lib/discoveryContinuation';

export default function RecentDiscovery({ mode, palette, onUse }) {
  const userId = useAuthStore(s => s.user?.id);
  const session = useAuthStore(s => s._sessionGeneration);
  const enabled = useAuthStore(s => s.user?.preferences?.chat?.followUpSuggestionsEnabled !== false);
  const chats = useChatStore(s => s.chats);
  const [sending, setSending] = useState(null);
  const sendingRef = useRef(false);
  const candidates = useMemo(() => userId && enabled ? recentDiscoveryIdeas(chats, mode) : [], [chats, mode, userId, enabled]);
  const ideas = useDiscoveryContinuations(candidates, mode, enabled);
  if (!ideas.length) return null;
  const resume = idea => {
    if (sendingRef.current) return;
    sendingRef.current = true; setSending(idea.id);
    queueDiscoveryContinuation({ conversationId: idea.conversationId, userId, session, prompt: idea.prompt });
    onUse?.(idea, 'used');
    router.push(`/c/${idea.conversationId}`);
  };
  return <View style={styles.section}>
    <Text size="s" weight="medium" color="secondary">Recent chats</Text>
    <View style={styles.cards}>{ideas.map(idea => <View key={idea.id} style={[styles.card, { borderColor: palette.border, backgroundColor: palette.surface }]}>
      <Pressable accessibilityRole="link" accessibilityLabel={`Open previous chat: ${idea.description}`}
        accessibilityHint="Opens the existing conversation without sending a message."
        onPress={() => router.push(`/c/${idea.conversationId}`)}
        style={({ hovered, pressed }) => [styles.chat, { opacity: pressed ? 0.7 : 1, backgroundColor: hovered ? palette.hover : 'transparent' }]}>
        <View style={[styles.chatIcon, { backgroundColor: palette.preview }]}><Icon name="comments" size={17} color={palette.icon} /></View>
        <View style={styles.chatTitle}>
          <Text size="xxs" color="secondary">Previous chat</Text>
          <Text size="s" weight="medium" numberOfLines={2}>{idea.description}</Text>
        </View>
        <Icon name="chevron-right" size={10} color={palette.icon} />
      </Pressable>
      <View style={[styles.message, { backgroundColor: palette.preview }]}>
        <Text size="xxs" color="secondary">Suggested next message</Text>
        <Text size="s" style={styles.prompt}>{idea.prompt}</Text>
      </View>
      <Pressable accessibilityRole="button" accessibilityLabel={`Send and continue previous chat: ${idea.description}`}
        accessibilityHint={`Sends this message in that conversation: ${idea.prompt}`}
        disabled={Boolean(sending)} accessibilityState={{ disabled: Boolean(sending) }}
        onPress={() => resume(idea)} style={({ hovered, pressed }) => [styles.send, { backgroundColor: hovered ? palette.hover : 'transparent', opacity: pressed ? 0.7 : 1 }]}>
        <Text size="s" weight="semibold">{sending === idea.id ? 'Opening previous chat…' : 'Send & continue chat'}</Text>
        <Icon name="arrow-right" size={13} color={palette.icon} />
      </Pressable>
    </View>)}</View>
  </View>;
}
const styles = StyleSheet.create({
  section: { gap: 16, marginBottom: 24 },
  cards: { flexDirection: 'row', flexWrap: 'wrap', gap: 12 },
  card: { flexGrow: 1, flexBasis: 260, minWidth: 0, borderWidth: 1, borderRadius: 20, padding: 16, gap: 16 },
  chat: { flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 52, borderRadius: 10 },
  chatIcon: { width: 36, height: 36, borderRadius: 11, alignItems: 'center', justifyContent: 'center' },
  chatTitle: { flex: 1, minWidth: 0, gap: 4 },
  message: { flexGrow: 1, borderRadius: 14, borderBottomRightRadius: 4, padding: 14, gap: 8 },
  prompt: { lineHeight: 22 },
  send: { minHeight: 40, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 10, paddingHorizontal: 4, borderRadius: 8 },
});
